import moment from "moment";
import Lottie from "react-lottie";
import { useEffect } from "react";
import { Tooltip } from "react-tooltip";
import React, { useState, useCallback } from "react";
import Badge from "../components/Badge";
import ApiServices from "../api/services";
import ReactPaginate from "react-paginate";
import { mediaUrl, baseUrl, googleApiKey } from "../utils/config";
import Heading from "../components/Heading";
import Spinner from "../components/Spinner";
import Skeleton from "react-loading-skeleton";
import { useMediaQuery } from "react-responsive";
import { images } from "../assets/styles/Images";
import InputField from "../components/InputField";
import RangePicker from "../components/RangePicker";
import { useLoggedContext } from "../context/provider";
import { useSortableData } from "../hooks/useSortableData";
import ChangeCardModal from "../components/ChangeCardModal";
import animationData from "../assets/lotties/empty-folder";
import ConfirmationModal from "../components/ConfirmationModal";
import { validatePartialForm } from "../helperFunctions/helper";
import SubscriptionPlansModal from "../components/SubscriptionPlansModal";
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon } from "@heroicons/react/24/solid";
import Select from "react-select";
import countryList from "react-select-country-list";
import { useMemo } from "react";
import ReciptModal from "../components/ReciptModal";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import axios from "axios";

// Custom places autocomplete hook
const usePlacesAutocomplete = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Clear suggestions
  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setShowSuggestions(false);
  }, []);

  // Function to create a proxy URL for Google API calls
  const createProxyUrl = (googleApiUrl) => {
    // Option 1: Use a free CORS proxy service (for development only)
    return `https://corsproxy.io/?${encodeURIComponent(googleApiUrl)}`;
  };

  // Fetch suggestions with debounce
  const debouncedFetchSuggestions = useCallback(
    (() => {
      let timeoutId = null;
      return (input) => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        
        if (!input || input.length < 3) {
          clearSuggestions();
          return;
        }
        
        timeoutId = setTimeout(async () => {
          setLoading(true);
          setError(null);
          
          try {
            // Use proxy for Google Places API
            const googleApiUrl = `https://maps.googleapis.com/maps/api/place/autocomplete/json?input=${encodeURIComponent(input)}&types=address&key=${googleApiKey}`;
            const proxyUrl = createProxyUrl(googleApiUrl);
            
            const response = await axios.get(proxyUrl);
            
            if (response.data && response.data.predictions) {
              setSuggestions(response.data.predictions);
              setShowSuggestions(true);
            } else {
              setSuggestions([]);
              setError("No suggestions found");
            }
          } catch (error) {
            console.error("Error fetching address suggestions:", error);
            setError("Failed to fetch address suggestions");
            setSuggestions([]);
          } finally {
            setLoading(false);
          }
        }, 300);
      };
    })(),
    [clearSuggestions]
  );

  // Get place details
  const getPlaceDetails = useCallback(async (placeId) => {
    if (!placeId) return null;

    try {
      // Use proxy for Google Places Details API
      const googleApiUrl = `https://maps.googleapis.com/maps/api/place/details/json?place_id=${placeId}&fields=address_components,formatted_address,geometry&key=${googleApiKey}`;
      const proxyUrl = createProxyUrl(googleApiUrl);
      
      const response = await axios.get(proxyUrl);

      if (response.data && response.data.result) {
        return response.data.result;
      }
      return null;
    } catch (error) {
      console.error("Error fetching place details:", error);
      setError("Failed to fetch place details");
      return null;
    }
  }, []);

  return {
    suggestions,
    showSuggestions,
    loading,
    error,
    debouncedFetchSuggestions,
    getPlaceDetails,
    clearSuggestions,
  };
};

// table head
const TABLE_HEAD_HISTORY = ["Date", "Package", "Price", "Status", "Recipt"];

const Profile = () => {
  // Context
  const { loading, setLoading, openSuccessModal, setBtnLoading, btnLoading, user, updateUser, setProfileData, profileData } = useLoggedContext();

  // useStates
  const [errors, setErrors] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [selectedFileError, setSelectedFileError] = useState("");
  const [message, setMessage] = useState({ text: "", color: "" });

  // Add new state variables for occupation, city, state, postal code
  const [occupation, setOccupation] = useState(null);
  const [occupations, setOccupations] = useState([]);
  const [occupationsLoading, setOccupationsLoading] = useState(true);
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [addressNewError, setAddressNewError] = useState("");
  const [occupationError, setOccupationError] = useState("");
  const [cityError, setCityError] = useState("");
  const [stateError, setStateError] = useState("");
  const [postalCodeError, setPostalCodeError] = useState("");

  // Data
  const preData = profileData?.user?.nursery[0];

  const [nurseryTransactionData, setNurseryTransactionData] = useState([]);

  // Pagination
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // Modal
  const [openChangeCardModal, setOpenChangeCardModal] = useState(false);
  const [openSubscriptionPlansModal, setOpenSubscriptionPlansModal] = useState({ open: false, data: null });
  const [openReciptModal, setOpenReciptModal] = useState({ open: false, data: null });
  const [openCancelSubcriptionModal, setOpenCancelSubcriptionModal] = useState({ open: false, label: "" });

  // Date
  const [employeeTransactionHistoryRange, setEmployeeTransactionHistoryRange] = useState({ startDate: null, endDate: null });

  const [formData, setFormData] = useState({
    contactPerson: "",
    phoneNumber: "",
    numberOfEmployees: "",
    address: "",
    firstAiderName: "",
    settingWellbeing: "",
  });

  const [country, setCountry] = useState(null);
  const [countryErr, setCountryErr] = useState("");
  const countriesList = useMemo(() => countryList().getData(), []);

  // Places autocomplete for address
  const {
    suggestions: addressSuggestions,
    showSuggestions: showAddressSuggestions,
    loading: addressLoading,
    error: addressError,
    debouncedFetchSuggestions,
    getPlaceDetails,
    clearSuggestions,
  } = usePlacesAutocomplete();

  // Destructure Values From formData
  const { contactPerson, phoneNumber, numberOfEmployees, address, firstAiderName, settingWellbeing } = formData;

  // Fetch occupations
  const fetchOccupations = async () => {
    setOccupationsLoading(true);
    setOccupations([]);

    try {
      const response = await axios.get(`${baseUrl}employee/occupations`);

      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        const occupationsArray = response.data.data;

        if (occupationsArray.length === 0) {
          console.warn("API returned empty occupations list");
        } else {
          const formattedData = occupationsArray.map((occupation) => ({
            value: occupation.id,
            label: occupation.name,
          }));

          setOccupations(formattedData);
          setOccupationError("");
        }
      } else {
        console.error("Unexpected API response structure:", response);
        setOccupationError("Unexpected data format from server");
      }
    } catch (error) {
      console.error("Failed to fetch occupations:", error);
      const errorMessage = error.response?.data?.message || "Failed to load occupations. Please try again.";
      setOccupationError(errorMessage);
    } finally {
      setOccupationsLoading(false);
    }
  };

  // Handle address selection from autocomplete
  const handleAddressSelect = async (placeId, description) => {
    setFormData({ ...formData, address: description });
    setAddressNewError("");
    clearSuggestions();

    try {
      const placeDetails = await getPlaceDetails(placeId);
      
      if (placeDetails) {
        const addressComponents = placeDetails.address_components || [];
        
        // Initialize variables for address parts
        let streetNumber = "";
        let route = "";
        let cityValue = "";
        let stateValue = "";
        let postalCodeValue = "";
        let countryCode = "";
        let countryName = "";

        // Extract address components
        addressComponents.forEach((component) => {
          const types = component.types;

          if (types.includes("street_number")) {
            streetNumber = component.long_name;
          } else if (types.includes("route")) {
            route = component.long_name;
          } else if (types.includes("locality") || types.includes("postal_town")) {
            cityValue = component.long_name;
          } else if (types.includes("administrative_area_level_1")) {
            stateValue = component.long_name;
          } else if (types.includes("postal_code")) {
            postalCodeValue = component.long_name;
          } else if (types.includes("country")) {
            countryName = component.long_name;
            countryCode = component.short_name;
          }
        });

        // Create a street address if we have the components
        let formattedAddress = description;
        if (streetNumber && route) {
          formattedAddress = `${streetNumber} ${route}`;
        } else if (route) {
          formattedAddress = route;
        }

        // Find matching country in our list
        const countryOption = countriesList.find(
          (c) => c.label === countryName || c.value === countryCode
        );

        // Update all the form fields
        setFormData({
          ...formData,
          address: formattedAddress
        });
        setCity(cityValue || "");
        setState(stateValue || "");
        setPostalCode(postalCodeValue || "");
        
        if (countryOption) {
          setCountry(countryOption);
          setCountryErr("");
        }

        // Clear any errors
        setAddressNewError("");
        setCityError("");
        setStateError("");
        setPostalCodeError("");
      }
    } catch (error) {
      console.error("Error fetching place details:", error);
      setMessage({
        text: "Failed to get address details. Please try again or enter manually.",
        color: "#ed4337",
      });
    }
  };

  // Handle Change
  const handleChange = (event) => {
    const { name, value } = event.target;
    const trimmedValue = value.trimStart();

    setErrors({ ...errors, [name]: "" });
    
    if (name === "address") {
      debouncedFetchSuggestions(trimmedValue);
    }
    
    setFormData({ ...formData, [name]: trimmedValue });
  };

  // Cancel Edit Mode
  const handleCancelEditMode = () => {
    setEditMode(false);
    setErrors({});
    clearSuggestions();
  };

  // Table Sorting
  const { items, requestSort, sortConfig } = useSortableData(nurseryTransactionData);

  // Media Queries
  const isLaptop = useMediaQuery({ minWidth: 1024 });
  const isLaptopMedium = useMediaQuery({ minWidth: 1536 });
  const isLargeScreenLaptop = useMediaQuery({ minWidth: 1700 });
  const itemsPerPage = isLargeScreenLaptop ? 8 : isLaptopMedium ? 5 : isLaptop ? 6 : 7;

  // Get Profile Details
  const getProfileDetail = () => {
    setLoading(true);

    ApiServices.profile
      .getProfile()
      .then((res) => {
        const { data, message } = res;
        if (data.code === 200) {
          setLoading(false);
          setProfileData(data?.data);
        }
      })
      .catch((err) => {});
  };

  // Handle Submit Profile
  const handleSubmit = () => {
    setMessage({ text: "", color: "" });
    const errors = validatePartialForm(formData);
    const { contactPerson, phoneNumber, address, firstAiderName, settingWellbeing } = errors;
    setErrors(errors);

    // Additional field validation
    let isValid = true;
    
    // Check if any errors from validatePartialForm
    if (contactPerson || phoneNumber || address || firstAiderName || settingWellbeing) {
      isValid = false;
    }
    
    if (!city) {
      setCityError("Required");
      isValid = false;
    }
    
    if (!state) {
      setStateError("Required");
      isValid = false;
    }
    
    if (!postalCode) {
      setPostalCodeError("Required");
      isValid = false;
    }
    
    if (!occupation) {
      setOccupationError("Required");
      isValid = false;
    }
    
    if (!country) {
      setCountryErr("Required");
      isValid = false;
    }

    if (isValid) {
      const payload = new FormData();

      if (selectedFile instanceof File) {
        payload.append("logo", selectedFile);
      }

      payload.append("mode", "setting_user");
      payload.append("contact_person_name", formData.contactPerson);
      payload.append("phone_number", formData.phoneNumber);
      payload.append("address", formData.address);
      payload.append("mental_health_first_aider_name", formData.firstAiderName);
      payload.append("setting_wellbeing_menifesto", formData.settingWellbeing);
      payload.append("country", country ? country?.label : null);
      
      // Add new fields to payload
      payload.append("city", city);
      payload.append("state", state);
      payload.append("postal_code", postalCode);
      payload.append("occupation_id", occupation?.value);

      setBtnLoading(true);

      ApiServices.profile
        .updateProfile(payload)
        .then((res) => {
          const { data, message } = res;

          if (data.code === 200) {
            setBtnLoading(false);
            setEditMode(false);
            updateUser(data.data);
            getProfileDetail();
            openSuccessModal({
              title: "Success!",
              message: "Detail updated successfully.",
              onPress: (close) => {
                close();
                getProfileDetail();
              },
            });
          }
        })
        .catch((err) => {
          setBtnLoading(false);
          setMessage({
            text: err?.response?.data?.message || "An error occurred.",
            color: "#ed4337",
          });
        });
    }
  };

  // Get Transaction Listing
  const getNurseryTransactionListing = () => {
    setBillingLoading(true);
    let payload = {
      page: currentPage,
      records_no: itemsPerPage,
      start_date: employeeTransactionHistoryRange?.startDate ? employeeTransactionHistoryRange.startDate : null,
      end_date: employeeTransactionHistoryRange?.endDate ? employeeTransactionHistoryRange.endDate : null,
    };

    ApiServices.profile
      .getNurseryTransaction(payload)
      .then((res) => {
        const { data, message } = res;

        if (data.code === 200) {
          setBillingLoading(false);
          setNurseryTransactionData(data?.data?.data);
          setCurrentPage(data?.data?.current_page);
          setTotalPages(Math.ceil(data?.data?.total / data?.data?.per_page));
        }
      })
      .catch((err) => {
        setBillingLoading(false);
      });
  };

  // Cancel Subscription Plan
  const handleCancelSubscription = () => {
    setBtnLoading(true);
    ApiServices.profile
      .cancelSubscriptionPlan()
      .then((res) => {
        const { data, message } = res;

        if (data.code === 200) {
          setOpenCancelSubcriptionModal({ open: false, label: "" });
          setBtnLoading(false);
          openSuccessModal({
            title: "Success!",
            message: "Your cancel subscription request has been submitted ",
            onPress: (close) => {
              close();
              getProfileDetail();
            },
          });
        }
      })
      .catch((err) => {});
  };
  
  // Pagination
  const handleTransactionHisttoryChange = ({ selected }) => {
    setCurrentPage(selected + 1);
  };

  // useEffects
  useEffect(() => {
    getProfileDetail();
  }, []);

  useEffect(() => {
    if (editMode) {
      fetchOccupations();
    }
  }, [editMode]);

  useEffect(() => {
    getNurseryTransactionListing();
  }, [currentPage, employeeTransactionHistoryRange]);

  // On Edit
  useEffect(() => {
    if (profileData) {
      setFormData({
        contactPerson: preData?.contact_person_name || "N/A",
        phoneNumber: preData?.phone_number || "0000",
        address: preData?.address || "N/A",
        firstAiderName: preData?.mental_health_first_aider_name ? preData?.mental_health_first_aider_name : "N/A",
        settingWellbeing: preData?.setting_wellbeing_menifesto ? preData?.setting_wellbeing_menifesto : "N/A",
      });
      
      // Set country
      if (preData?.country) {
        const selectedCountry = countriesList?.find((option) => option?.label === preData?.country);
        setCountry(selectedCountry);
      } else {
        setCountry(null);
      }
      
      // Set new fields from profile data
      setCity(preData?.city || "");
      setState(preData?.state || "");
      setPostalCode(preData?.postal_code || "");
      
      // Set occupation if available
      if (preData?.occupation) {
        setOccupation({
          value: preData?.occupation?.id,
          label: preData?.occupation?.name
        });
      } else {
        setOccupation(null);
      }
    }
  }, [profileData]);

  // Lottie Option
  const emptyFolderAnimation = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  return (
    <>
      <div className="grid min-h-[83vh] grid-cols-12 gap-4">
        <div className="col-span-12 space-y-4 xl:col-span-5">
          <div className="card">
            <div className="divide-y divide-grayColor200 px-4">
              {loading ? (
                <div className="flex items-center gap-x-4 pt-4 pb-8">
                  <Skeleton className="!rounded-full" count={1} height={100} width={100} />
                  <Skeleton className="!rounded-xl" count={1} height={100} width={300} />
                </div>
              ) : (
                <div className="flex items-center gap-x-4 pt-4 pb-8">
                  <div className="relative">
                    <div className="h-16 w-16 rounded-full 3xl:h-36 3xl:w-36">
                      {selectedFile ? (
                        <>
                          <img src={URL.createObjectURL(selectedFile)} className="h-full w-full rounded-full " alt="Upload Image" />
                        </>
                      ) : (
                        <>
                          <img
                            src={profileData?.user?.nursery[0]?.logo ? mediaUrl + profileData?.user?.nursery[0]?.logo : images.placeholder}
                            className="h-full w-full rounded-full object-cover "
                            alt="Upload Image"
                          />
                        </>
                      )}
                      {selectedFileError && <span className="ml-5 text-sm text-red-600">{selectedFileError}</span>}
                    </div>
                    {editMode && (
                      <>
                        <div className="absolute -bottom-2 -right-6 rounded-full">
                          <input
                            type="file"
                            accept="image/png, image/jpeg, image/jpg"
                            onChange={(e) => {
                              setSelectedFile(e.target.files[0]);
                              setSelectedFileError("");
                            }}
                            style={{ display: "none" }}
                            id="imageInput"
                          />
                          <label htmlFor="imageInput">
                            <img src={images.Camera} alt="Camera Icon" className="h-16 w-16 cursor-pointer" />
                          </label>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-x-4">
                      <h2 className="text-2xl font-medium text-black 3xl:text-3xl">{profileData?.user?.nursery[0]?.name}</h2>
                      <Badge status={profileData?.user?.status === 1 ? "Active" : ""} />
                    </div>
                    <h3 className="text-xl text-grayColor300 3xl:text-2xl">{profileData?.user?.email}</h3>
                  </div>
                </div>
              )}
              <div className="space-y-4 pt-6 pb-2">
                {/* Heading and edit button */}
                <div className="flex items-baseline justify-between ">
                  <div className="flex items-center gap-x-4">
                    <Heading
                      heading="Profile"
                      icon
                      handleRefresh={() => {
                        getProfileDetail();
                      }}
                    />
                  </div>

                  {loading ? (
                    <Skeleton count={1} height={50} width={100} />
                  ) : editMode ? (
                    <div className="flex gap-x-4">
                      <h2 onClick={handleCancelEditMode} className="cursor-pointer text-base font-medium text-secondary underline underline-offset-2">
                        Cancel
                      </h2>
                      <button
                        onClick={handleSubmit}
                        className="edit-button text-base font-medium text-accent underline underline-offset-2"
                        type="button"
                      >
                        {btnLoading ? <Spinner /> : "Save"}
                      </button>
                    </div>
                  ) : (
                    <>
                      {(user?.user?.permissions?.includes("profile-edit") || user?.user?.role === "setting_user") && (
                        <button
                          onClick={() => setEditMode(true)}
                          className="edit-button text-base font-medium text-secondary underline underline-offset-2"
                          type="button"
                        >
                          Edit
                        </button>
                      )}
                    </>
                  )}
                </div>
                {/* End Heading and edit button */}

                {/* Message display */}
                {message.text && (
                  <div className="text-center">
                    <p className="text-sm font-medium" style={{ color: message.color || "#ed4337" }}>
                      {message.text}
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 items-center">
                  <h2 className="text-sm text-grayColor300">Contact Person</h2>

                  {loading ? (
                    <Skeleton count={1} height={50} />
                  ) : (
                    <InputField
                      type="text"
                      name="contactPerson"
                      value={contactPerson}
                      onChange={handleChange}
                      error={errors.contactPerson}
                      readOnly={!editMode && true}
                    />
                  )}
                </div>
                <div className="grid grid-cols-2 items-center">
                  <h2 className="text-sm text-grayColor300">Phone</h2>

                  {loading ? (
                    <Skeleton count={1} height={50} />
                  ) : (
                    <InputField
                      type="text"
                      name="phoneNumber"
                      value={phoneNumber}
                      onChange={handleChange}
                      error={errors.phoneNumber}
                      readOnly={!editMode && true}
                    />
                  )}
                </div>

                {/* New field: Occupation */}
                <div className="grid grid-cols-2 items-center">
                  <h2 className="text-sm text-grayColor300">Occupation</h2>
                  {loading ? (
                    <Skeleton count={1} height={50} />
                  ) : editMode ? (
                    <>
                      {occupationError && <span className="text-xs text-red-500">{occupationError}</span>}
                      <Select
                        options={occupations}
                        value={occupation}
                        name="occupation"
                        onChange={(value) => {
                          setOccupation(value);
                          setOccupationError("");
                        }}
                        styles={customStyles}
                        placeholder={
                          occupationsLoading
                            ? "Loading occupations..."
                            : occupations.length === 0
                            ? "No occupations found"
                            : "Type to search occupation"
                        }
                        isLoading={occupationsLoading}
                        noOptionsMessage={() => "No occupations found"}
                      />
                    </>
                  ) : (
                    <InputField type="text" value={occupation?.label || "N/A"} readOnly={true} />
                  )}
                </div>

                {/* Address with autocomplete */}
                <div className="grid grid-cols-2 items-start">
                  <h2 className="text-sm text-grayColor300">Address</h2>

                  {loading ? (
                    <Skeleton count={1} height={50} />
                  ) : (
                    <div className="relative">
                      <InputField
                        type="text"
                        name="address"
                        value={address}
                        error={errors.address || addressError}
                        onChange={handleChange}
                        readOnly={!editMode && true}
                      />
                      
                      {/* Address suggestions dropdown */}
                      {editMode && showAddressSuggestions && addressSuggestions.length > 0 && (
                        <div className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg">
                          <ul>
                            {addressSuggestions.map((suggestion) => (
                              <li
                                key={suggestion.place_id}
                                className="cursor-pointer px-4 py-2 text-left text-sm hover:bg-gray-100"
                                onClick={() => handleAddressSelect(suggestion.place_id, suggestion.description)}
                              >
                                {suggestion.description}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* New field: City */}
                <div className="grid grid-cols-2 items-center">
                  <h2 className="text-sm text-grayColor300">City</h2>
                  {loading ? (
                    <Skeleton count={1} height={50} />
                  ) : (
                    <>
                      <div>
                        {cityError && <span className="text-xs text-red-500">{cityError}</span>}
                        <InputField
                          type="text"
                          name="city"
                          value={city}
                          onChange={(e) => {
                            setCity(e.target.value);
                            setCityError("");
                          }}
                          readOnly={!editMode && true}
                        />
                      </div>
                    </>
                  )}
                </div>

                {/* New field: State */}
                <div className="grid grid-cols-2 items-center">
                  <h2 className="text-sm text-grayColor300">State / County / Province</h2>
                  {loading ? (
                    <Skeleton count={1} height={50} />
                  ) : (
                    <>
                      <div>
                        {stateError && <span className="text-xs text-red-500">{stateError}</span>}
                        <InputField
                          type="text"
                          name="state"
                          value={state}
                          onChange={(e) => {
                            setState(e.target.value);
                            setStateError("");
                          }}
                          readOnly={!editMode && true}
                        />
                      </div>
                    </>
                  )}
                </div>

                {/* New field: Postal Code */}
                <div className="grid grid-cols-2 items-center">
                  <h2 className="text-sm text-grayColor300">Postal Code</h2>
                  {loading ? (
                    <Skeleton count={1} height={50} />
                  ) : (
                    <>
                      <div>
                        {postalCodeError && <span className="text-xs text-red-500">{postalCodeError}</span>}
                        <InputField
                          type="text"
                          name="postalCode"
                          value={postalCode}
                          onChange={(e) => {
                            setPostalCode(e.target.value);
                            setPostalCodeError("");
                          }}
                          readOnly={!editMode && true}
                        />
                      </div>
                    </>
                  )}
                </div>

                <div className="grid grid-cols-2 items-center">
                  <h2 className="text-sm text-grayColor300">Country</h2>
                  {loading ? (
                    <Skeleton count={1} height={50} />
                  ) : editMode ? (
                    <>
                      {countryErr && <span className="text-xs text-red-500">{countryErr}</span>}

                      <Select
                        options={countriesList}
                        value={country}
                        name="country"
                        onChange={(value) => {
                          setCountry(value);
                          setCountryErr("");
                        }}
                        styles={customStyles}
                        placeholder="Type to search your country"
                        className="mt-2"
                      />
                    </>
                  ) : (
                    <InputField type="text" value={country?.label || "N/A"} readOnly={true} />
                  )}
                </div>

                <div className="grid grid-cols-2 items-center">
                  <h2 className={`text-sm text-grayColor300 ${!editMode && "pt-4"}`}>Mental Health First Aider name</h2>

                  {loading ? (
                    <Skeleton count={1} height={50} />
                  ) : (
                    <InputField
                      type="text"
                      name="firstAiderName"
                      value={firstAiderName}
                      onChange={handleChange}
                      readOnly={!editMode && true}
                      error={errors.firstAiderName}
                    />
                  )}
                </div>
                <div className={`grid grid-cols-2  ${editMode ? "items-center" : "items-start"}`}>
                  <h2 className={`text-sm text-grayColor300 ${!editMode && "pt-8"}`}>Setting Wellbeing Manifesto</h2>

                  {loading ? (
                    <Skeleton count={1} height={50} />
                  ) : (
                    <div className="space-y-1">
                      <span className="text-xs text-red-500">{errors.settingWellbeing}</span>
                      <textarea
                        rows={3}
                        name="settingWellbeing"
                        value={settingWellbeing}
                        className={` ${errors.settingWellbeing ? "!border-red-500 focus:ring-red-500" : "border-primary-color-300 "} w-full rounded-2xl border py-3 text-primary-color-200 placeholder:text-primary-color-100`}
                        onChange={handleChange}
                        readOnly={!editMode && true}
                      >
                        {" "}
                      </textarea>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-span-12 flex flex-col space-y-4 xl:col-span-7">
          <div className="card grid grid-cols-2 gap-4">
            <div className="space-y-8">
              <div className="flex">
                <Heading
                  heading="Subscription"
                  subheading="Your subscription plan details"
                  icon
                  handleRefresh={() => {
                    getProfileDetail();
                  }}
                />
              </div>
              <div className="grid grid-cols-1 gap-5">
                <div className={`space-y-4 rounded-xl border border-gray-200 p-6 ${user?.user?.role === "setting_user_employee" && "opacity-50"}`}>
                  <div className="">
                    <Heading heading="Payment Method" />
                    <img className="h-6 shrink-0" src={images.Stripe} alt="Stripe" />
                  </div>
                  <div className="flex items-center justify-between ">
                    <div>
                      <h2 className="text-lg font-semibold text-primary-color-100">
                        {profileData?.user?.card[0]?.card_type}*******{profileData?.user?.card[0]?.card_last_four_digit}
                      </h2>
                    </div>

                    {(user?.user?.role === "setting_user" || user?.user?.permissions?.includes("profile-update-payment-method")) && (
                      <span
                        onClick={() => setOpenChangeCardModal(true)}
                        className="cursor-pointer text-base font-semibold text-secondary underline underline-offset-4"
                      >
                        Change Card
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {loading ? (
              <Skeleton className="mt-8" count={1} height={200} />
            ) : (
              <div className="space-y-4 rounded-xl border border-gray-200 p-6">
                <div>
                  
                  <div className="grid grid-cols-2 gap-x-3 gap-y-5 pb-5">
                    <div className="flex items-center gap-x-2">
                      <h2 className="  text-left text-base font-semibold text-secondary">Plan:</h2>
                      <h2 className="whitespace-nowrap   text-base text-secondary-color">
                        {" "}
                        {profileData?.user?.subscription?.find((sub) => sub.status === "active")?.plan_name}
                      </h2>
                    </div>
                    <div className="flex items-center gap-x-2">
                      <h2 className="  text-left text-base font-semibold text-secondary">Billing Cycle:</h2>
                      <h2 className="whitespace-nowrap   text-base text-secondary-color">
                        {" "}
                        {profileData?.user?.subscription?.find((sub) => sub.status === "active")?.plan_type}
                      </h2>
                    </div>
                    <div className="flex items-center gap-x-2">
                      <h2 className="  text-left text-base font-semibold text-secondary">Seats:</h2>
                      <h2 className="whitespace-nowrap   text-base text-secondary-color">
                        {" "}
                        {profileData?.user?.subscription?.find((sub) => sub.status === "active")?.max_user}
                      </h2>
                    </div>
                    <div className="flex items-center gap-x-2">
                      <h2 className="  text-left text-base font-semibold text-secondary">Price:</h2>
                      <h2 className="whitespace-nowrap   text-base text-secondary-color">
                        {" "}
                        £{profileData?.user?.subscription?.find((sub) => sub.status === "active")?.price}
                      </h2>
                    </div>
                  </div>
                  {profileData?.user?.subscription[0]?.cancel_status === 1 ? (
                    <h2 className="mt-2 flex gap-x-1 text-xs text-primary-color-400 xl:text-xs 2xl:text-sm">
                      Your subscription is valid until{" "}
                      <span className="font-medium">
                        <p id={`date`} className="text-xs font-normal text-primary-color-200 3xl:text-sm">
                          {moment.unix(parseFloat(profileData?.user?.subscription[0]?.end_date)).format("D MMM YYYY")}
                        </p>
                        <Tooltip
                          anchorSelect={`#date`}
                          id={`date-tooltip`}
                          offset={5}
                          className="border border-gray-100 !bg-white !text-primary-color-100 !opacity-100 shadow-card"
                          classNameArrow="hidden"
                          effect="solid"
                          place="bottom"
                        >
                          {/* Tooltip Content */}
                          <span className="font-medium">
                            {moment.unix(parseFloat(profileData?.user?.subscription[0]?.end_date)).format("D MMM YYYY h:mm A")}
                          </span>
                        </Tooltip>
                      </span>
                    </h2>
                  ) : (
                    <>
                      <h2 className="mt-2 flex gap-x-1 text-xs text-primary-color-400 xl:text-xs 2xl:text-sm">
                        Next Payment: <span className="font-medium">£{profileData?.user?.subscription[0]?.price}</span> on
                        <span className="font-medium">
                          <p id={`date`} className="text-xs font-normal text-primary-color-200 3xl:text-sm">
                            {moment.unix(parseFloat(profileData?.user?.subscription[0]?.end_date)).format("D MMM YYYY")}
                          </p>
                          <Tooltip
                            anchorSelect={`#date`}
                            id={`date-tooltip`}
                            offset={5}
                            className="border border-gray-100 !bg-white !text-primary-color-100 !opacity-100 shadow-card"
                            classNameArrow="hidden"
                            effect="solid"
                            place="bottom"
                          >
                            {/* Tooltip Content */}
                            <span className="font-medium">
                              {moment.unix(parseFloat(profileData?.user?.subscription[0]?.end_date)).format("D MMM YYYY h:mm A")}
                            </span>
                          </Tooltip>
                        </span>
                        (VAT may apply)
                      </h2>
                      {profileData?.user?.free_trial === 1 && (
                        <h2 className="pt-1 text-sm text-primary-color-400">
                          Free trial ending at: {moment.unix(parseFloat(profileData?.user?.subscription[0]?.end_date)).format("D MMM YYYY")}
                        </h2>
                      )}
                    </>
                  )}
                </div>
                <div className="flex items-center justify-end gap-x-4 pt-5">
                  {profileData?.user?.subscription[0]?.cancel_status === 0 ? (
                    <>
                      <button
                        className="text-sm font-medium text-red-500 underline underline-offset-2 3xl:text-base"
                        type="button"
                        onClick={() =>
                          setOpenCancelSubcriptionModal({
                            open: true,
                            label: "Cancel",
                          })
                        }
                      >
                        Cancel Subscription
                      </button>
                      {(user?.user?.permissions?.includes("profile-change-plan") || user?.user?.role === "setting_user") && (
                        <button
                          onClick={() => setOpenSubscriptionPlansModal({ open: true, data: profileData?.user?.subscription[0]?.plan_id })}
                          className="rounded-full bg-secondary py-2.5 px-8 text-sm font-medium text-white  3xl:text-base"
                          type="button"
                        >
                          Change Plan
                        </button>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={() => setOpenSubscriptionPlansModal({ open: true, data: "" })}
                      className="rounded-full bg-secondary py-2.5 px-8 text-sm font-medium text-white  3xl:text-base"
                      type="button"
                    >
                      Buy Plan
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          {(user?.user?.permissions?.includes("profile-view-billing-history") || user?.user?.role === "setting_user") && (
            <div className="card flex-grow">
              <div className="flex justify-between">
                <div className="relative">
                  <Heading
                    heading="Billing History"
                    subheading="View plan history of packages"
                    icon
                    handleRefresh={() => {
                      getNurseryTransactionListing();
                    }}
                  />
                </div>
                <RangePicker setRangePicker={setEmployeeTransactionHistoryRange} width="xl:w-80" />
              </div>
              <div className="mt-4">
                <div className="-mx-6 mb-12 overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr>
                        {TABLE_HEAD_HISTORY?.map((head) => (
                          <th
                            key={head}
                            className="border-b border-gray-100 bg-white p-4 first:pl-6"
                            onClick={() => {
                              let sortKey;
                              if (head === "Date") {
                                sortKey = "created_at";
                              } else if (head === "Package") {
                                sortKey = "plan_name";
                              } else if (head === "Price") {
                                sortKey = "price";
                              } else {
                                sortKey = head.toLowerCase();
                              }
                              requestSort(sortKey);
                            }}
                          >
                            <p className="cursor-pointer whitespace-nowrap font-inter text-xs font-semibold leading-5 3xl:text-sm">
                              {head}
                              {sortConfig.key ===
                                (head === "Date"
                                  ? "created_at"
                                  : head === "Package"
                                    ? "plan_name"
                                    : head === "Price"
                                      ? "price"
                                      : head.toLowerCase()) && sortConfig.direction === "asc" ? (
                                <ChevronUpIcon className="inline-block h-4 w-3.5" />
                              ) : (
                                <ChevronDownIcon className="inline-block h-4 w-3.5" />
                              )}
                            </p>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {billingLoading ? (
                        <tr>
                          <td colSpan="6">
                            <Skeleton count={itemsPerPage} height={50} />
                          </td>
                        </tr>
                      ) : items.length > 0 ? (
                        items.map((item, index) => (
                          <tr key={item?.id} className="transition-all duration-300 even:bg-gray-50 hover:bg-secondary/10">
                            <td className="py-4 pl-4 3xl:pl-6 3xl:pr-4">
                              <p id={`date${index + 1}`} className="text-xs font-normal text-primary-color-200 3xl:text-sm">
                                {moment.unix(item?.created_at_unix).format("D MMM YYYY")}
                              </p>
                              <Tooltip
                                anchorSelect={`#date${index + 1}`}
                                id={`date-tooltip${index + 1}`}
                                offset={5}
                                className="border border-gray-100 !bg-white !text-primary-color-100 !opacity-100 shadow-card"
                                classNameArrow="hidden"
                                effect="solid"
                                place="bottom"
                              >
                                {/* Tooltip Content */}
                                <span>{moment.unix(item?.created_at_unix).format("D MMM YYYY h:mm A")}</span>
                              </Tooltip>
                            </td>
                            <td className="py-4 pl-4 3xl:px-4">
                              <p className="text-xs font-normal text-primary-color-200 3xl:text-sm">{item?.plan_name + "/" + item?.plan_type}</p>
                            </td>

                            <td className="py-4 pl-4 3xl:px-4">
                              <p className="text-xs font-normal text-primary-color-200 3xl:text-sm">£{item?.price}</p>
                            </td>
                            <td className="py-3 pl-4 pr-3 3xl:px-4">
                              <Badge status={item?.payment_status === "received" ? "Success" : "Failed"} />
                            </td>
                            <td className="py-3 pl-4 pr-3 3xl:px-4">
                              <span
                                onClick={() => setOpenReciptModal({ open: true, data: item })}
                                className="cursor-pointer text-xs font-normal text-secondary underline underline-offset-2 3xl:text-sm"
                              >
                                Receipt
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr className=" h-[300px]">
                          <td colSpan="6">
                            <Lottie options={emptyFolderAnimation} width={200} height={200} />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="absolute bottom-4">
                  <ReactPaginate
                    breakLabel="..."
                    pageRangeDisplayed={5}
                    pageCount={totalPages}
                    marginPagesDisplayed={2}
                    activeClassName="active"
                    nextClassName="item next"
                    forcePage={currentPage - 1}
                    renderOnZeroPageCount={null}
                    breakClassName="item break-me "
                    containerClassName="pagination"
                    previousClassName="item previous"
                    pageClassName="item pagination-page"
                    onPageChange={handleTransactionHisttoryChange}
                    nextLabel={<ChevronRightIcon className="h-5 w-5" />}
                    previousLabel={<ChevronLeftIcon className="h-5 w-5" />}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cancel Subscription Modal */}
      <ConfirmationModal
        isOpen={openCancelSubcriptionModal.open}
        label={openCancelSubcriptionModal.label}
        labelTwo="your subscription"
        handleSubmit={handleCancelSubscription}
        setIsOpen={(open) => setOpenCancelSubcriptionModal((prev) => ({ ...prev, open }))}
      />

      {/* Subscription Plans Modal */}
      <SubscriptionPlansModal
        isOpen={openSubscriptionPlansModal.open}
        data={openSubscriptionPlansModal.data}
        setIsOpen={(open) => setOpenSubscriptionPlansModal((prev) => ({ ...prev, open }))}
        onSuccess={getProfileDetail}
        updatePlan={true}
      />

      <ReciptModal
        data={openReciptModal.data}
        isOpen={openReciptModal.open}
        setIsOpen={(open) => setOpenReciptModal((prev) => ({ ...prev, open }))}
      />

      {/* Change Card Modal (VISA, MASTER) */}
      <ChangeCardModal isOpen={openChangeCardModal} setIsOpen={setOpenChangeCardModal} getProfile={getProfileDetail} changeCard={true}/>
    </>
  );
};

export default Profile;

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    height: "50px",
    minHeight: "50px",
    borderRadius: "4.375rem",
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    height: "50px",
    padding: "0 24px",
  }),
  input: (provided, state) => ({
    ...provided,
    margin: "0px",
    color: "rgb(91 91 91)",
  }),
  singleValue: (provided, state) => ({
    ...provided,
    color: "rgb(91 91 91)",
  }),
  indicatorsContainer: (provided, state) => ({
    ...provided,
    height: "50px",
  }),
  option: (provided, state) => ({
    ...provided,
    padding: "10px 12px",
  }),
  placeholder: (provided, state) => ({
    ...provided,
    fontSize: "12px",
  }),
  singleValue: (provided, state) => ({
    ...provided,
    fontSize: "14px",
    color: "#474747",
    fontWeight: "500",
  }),
};